import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      app: {
        title: 'SPARCC',
        subtitle: 'AI Medical Imaging Analysis System',
      },
      common: {
        loading: 'Loading...',
        toastLoadDetailFail: 'Failed to load details',
        confirm: 'Confirm',
        cancel: 'Cancel',
        close: 'Close',
        back: 'Back',
        aiDisclaimer: 'AI analysis results are for reference purposes only',
        // Additional common keys
        analysisInProgress: 'Analysis in progress, please wait...',
        fillRequiredFields: 'Please fill in required fields',
        loadFailed: 'Load failed',
        patientNameRequired: 'Patient name is required',
        doctorNameRequired: 'Doctor name is required',
        doctorIdRequired: 'Doctor ID is required'
      },
      language: {
        en: 'EN',
        zh: '繁體中文',
        'zh-CN': '简体中文'
      },
      homepage: {
        nav: {
          productInfo: 'Product Information',
          contact: 'Contact',
        },
        hero: {
          title: 'SPARCC',
          subtitle: 'Becoming Rheumatologists\' AI Co-Brain',
          description: 'Rheumatology Limited is committed to digitizing rheumatology care, significantly enhancing diagnostic efficiency and accuracy through advanced AI technologies, setting a new standard in the field.',
          downloadMac: 'Download for macOS',
          downloadWin: 'Download for Windows',
          downloadHarmony: 'Download for HarmonyOS',
          downloadHint: 'Supports macOS 12+ / Windows 10+ / HarmonyOS 4+',
          learnMore: 'Learn More',
          ctaDescription: 'Download SPARCC desktop application for medical imaging analysis',
          successRate: 'Success Rate'
        },
        features: {
          title: 'Platform Features',
          spine: {
            title: 'Spine Analysis',
            description: 'Comprehensive spine imaging analysis with SPARCC scoring for accurate inflammation assessment'
          },
          si: {
            title: 'SI Joint Analysis',
            description: 'Sacroiliac joint inflammation assessment with precise quadrant-based scoring'
          },
          analytics: {
            title: 'Advanced Analytics',
            description: 'Detailed reporting, statistical analysis, and comprehensive patient history tracking'
          },
          ai: {
            title: 'AI-Powered Detection',
            description: 'State-of-the-art artificial intelligence for accurate lesion detection and classification'
          },
          speed: {
            title: 'Rapid Processing',
            description: 'Fast analysis with results typically available within minutes of upload'
          },
          security: {
            title: 'Secure & Compliant',
            description: 'HIPAA-compliant data handling with enterprise-grade security measures'
          }
        },
        about: {
          title: 'About SPARCC Platform',
          description: 'SPARCC (Spondyloarthritis Research Consortium of Canada) is a leading medical imaging analysis platform that leverages advanced artificial intelligence to provide accurate and efficient assessment of spine and sacroiliac joint inflammation. Our platform is designed to support healthcare professionals in making informed diagnostic decisions.',
          highlightsTitle: 'Key Highlights',
          highlight1: 'Trusted by medical professionals worldwide',
          highlight2: 'Research-backed AI algorithms with clinical validation',
          highlight3: 'Rapid analysis with results in minutes',
          highlight4: 'Enterprise-grade security and HIPAA compliance',
          missionTitle: 'Our Mission',
          mission: 'To revolutionize medical imaging analysis by providing healthcare professionals with cutting-edge AI tools that enhance diagnostic accuracy, reduce analysis time, and ultimately improve patient outcomes in the field of rheumatology and spine care.'
        },
        visionMission: {
          title: 'Vision & Mission',
          subtitle: 'Becoming Rheumatologists\' AI Co-Brain',
          step1: {
            title: 'Digital Transformation',
            description: 'Rheumatology Limited is committed to digitizing rheumatology care, significantly enhancing diagnostic efficiency and accuracy through advanced AI technologies, setting a new standard in the field.'
          },
          step2: {
            title: 'RheumaScore™ Suite',
            description: 'Our RheumaScore™ AI scoring suite covers SPARCC Spine and SI Joint, offering instant and precise decision support to clinicians, revolutionizing the way rheumatology is practiced.'
          },
          step3: {
            title: 'Future Goals',
            description: 'Our goal is to become an indispensable AI co-brain for every rheumatologist and radiologist, driving the intelligent transformation of rheumatology care globally.'
          }
        },
        painPoints: {
          title: 'Pain Points & Cure',
          sparcc: {
            title: 'SPARCC scoring system',
            step1: 'Axial spondyloarthritis',
            step2: 'Chronic rheumatic disease',
            step3: 'Causing inflammation',
            assessment: 'Inflammation assessment',
            grading: 'Grading the sacroiliitis / spinal inflammation',
            scoring: 'SPARCC system using STIR sequence',
            note: 'SPARCC system, most common evaluation system'
          },
          traditional: {
            title: 'Two Pain Points of Manual SPARCC',
            inefficiency: {
              title: 'Inefficiency',
              description: 'Manual SPARCC scoring takes an average of 8–12 minutes per case, severely limiting diagnostic throughput and failing to meet the needs of fast-paced clinical environments'
            },
            reliability: {
              title: 'Low Reliability',
              description: 'Multi-center studies show an inter-rater reliability (κ) of only 0.62, leading to significant scoring discrepancies and undermining clinical decision-making accuracy.'
            }
          },
          ai: {
            title: 'Core Feature Highlights',
            automation: {
              title: 'Rapid Processing',
              description: 'Our AI system processes MRI-STIR images in just few seconds, segmenting vertebral units and bilateral SI joints automatically, providing immediate results.'
            },
            efficiency: {
              title: 'High Precision',
              description: 'The AI achieves an ICC of 0.80 for the spine and 0.83 for the SI joints, matching or exceeding human experts, ensuring reliable and reproducible scoring.'
            },
            precision: {
              title: 'Clinical Impact',
              description: 'By delivering immediate, objective results, our AI empowers clinicians to make faster and more accurate decisions, improving patient outcomes.'
            },
            objective: {
              title: 'Objective & Reproducible',
              description: 'AI system provides immediate, objective, and reproducible scoring results, offering reliable data support for clinical decision-making.'
            }
          }
        },
        productShowcase: {
          title: 'Product Feature Showcase',
          subtitle: 'Experience the powerful features of SPARCC AI Medical Imaging Analysis Platform',
          tabs: {
            spine: 'Spine Analysis',
            spineDesc: 'SPARCC spine inflammation assessment',
            si: 'SI Joint Analysis',
            siDesc: 'Sacroiliac joint inflammation scoring',
            system: 'System Interface',
            systemDesc: 'Platform operation interface showcase'
          },
          images: {
            spine1: {
              alt: 'Spine analysis result 1',
              title: 'Spine Segmentation & Scoring',
              description: 'AI automatically segments spinal segments, providing precise SPARCC scoring'
            },
            spine2: {
              alt: 'Spine analysis result 2',
              title: 'Spine Inflammation Heatmap',
              description: 'Visualization of inflammation areas to assist clinical diagnostic decisions'
            },
            si: {
              alt: 'SI joint analysis result',
              title: 'Sacroiliac Joint Scoring',
              description: 'Four-quadrant scoring system for precise assessment of sacroiliac joint inflammation'
            },
            sys1: {
              alt: 'System interface 1',
              title: 'Analysis Statistics Interface',
              description: 'Real-time analysis statistics with multi-language support'
            },
            sys2: {
              alt: 'System interface 2',
              title: 'Upload Analysis Interface',
              description: 'Supports multiple medical image formats with one-click upload analysis'
            }
          },
          highlights: {
            title: 'Core Feature Highlights',
            speed: {
              title: 'Rapid Analysis',
              description: 'Complete SPARCC scoring in 3 seconds, significantly improving diagnostic efficiency'
            },
            accuracy: {
              title: 'High Precision Scoring',
              description: 'ICC up to 0.80-0.83, highly consistent with expert scoring'
            },
            automation: {
              title: 'Full Automation',
              description: 'One-click upload, automatic segmentation, no manual intervention required'
            },
            visualization: {
              title: 'Visualization Reports',
              description: 'Heatmaps show inflammation areas, structured reports facilitate clinical decisions'
            }
          }
        },
        publication: {
          title: 'Academic Publications',
          subtitle: 'Research achievements based on SPARCC scoring system deep learning algorithms',
          authors: 'Authors',
          published: 'Published',
          pub1: {
            title: 'Deep learning algorithm of the SPARCC scoring system in SI joint MRI',
            journal: 'Journal of Magnetic Resonance Imaging'
          },
          pub2: {
            title: 'A deep neural network for MRI spinal inflammation in axial spondyloarthritis',
            journal: 'European spine journal'
          },
          pub3: {
            title: 'Deep learning algorithms for magnetic resonance imaging of inflammatory sacroiliitis in axial spondyloarthritis',
            journal: 'Rheumatology'
          },
          pub4: {
            title: 'Deep learning algorithm for semiquantification of spinal inflammation in axial spondyloarthritis',
            journal: 'RMD Open'
          },
          highlight: {
            title: 'Research Highlights',
            papers: 'Papers',
            period: 'Publication Period'
          }
        },
        productInfo: {
          title: 'Product Information',
          subtitle: 'Regulatory disclosure for our AI medical imaging platform',
          relatedNotes: {
            title: 'Related Notes',
            intro: 'The platform is intended for radiologists and rheumatologists. The AI assists in assessing inflammation of the spine and sacroiliac joints and automatically calculates SPARCC inflammation scores.',
            warning: 'AI analysis results are for reference by healthcare professionals only and must not replace formal clinical diagnosis by a physician.',
            validation: 'Algorithm validity has been verified in peer-reviewed clinical studies, listed below.',
          },
          algorithmTraining: {
            label: 'Algorithm Training Summary',
            brief: 'U-Net based lesion segmentation model; see the training summary alongside.',
            body: 'The AI model is a U-Net convolutional neural network that segments lesions in spinal MRI images. During training, three consecutive slices are combined into a single three-channel image, resized to 128 × 128 pixels. The loss function combines the Dice coefficient and weighted binary cross-entropy, with extra weight on lesion pixels so that small lesions are not missed. Predictions are then filtered with a 0.5 confidence threshold.',
            fullSummaryLabel: 'View full summary',
            fullText: 'This study developed an automated framework combining deep-learning-based image segmentation with rule-based image analysis to identify spinal MRI lesions and subsequently derive imaging features related to SPARCC scoring.'
              + '\n\nThe lesion segmentation component uses a pre-trained U-Net model. Input images are resized to 128 × 128 pixels. Three consecutive slices were mapped to separate colour channels to generate a “fake-colour” image, which was then paired with the corresponding lesion mask for model inference and performance evaluation. The model framework includes the Dice coefficient, weighted binary cross-entropy, and a combined custom loss function. A higher weight is assigned to lesion pixels to compensate for the class imbalance between lesion and background pixels. A post-processing threshold of 0.5 is subsequently applied to remove low-confidence predicted pixels.'
              + '\n\nSegmentation performance is primarily evaluated using the Dice coefficient. A confusion matrix is additionally generated to calculate true-positive, true-negative, false-positive, and false-negative results. Receiver operating characteristic (ROC) analysis and the area under the ROC curve (AUC) are used to assess the ability of the algorithm to distinguish images with and without lesions. At the lesion level, individual contours from the ground-truth and predicted masks are compared according to their spatial overlap, allowing evaluation of lesion detection and matching performance.'
              + '\n\nFollowing lesion segmentation, rule-based image-processing procedures are applied to derive SPARCC-related scores. In the first scoring stage, intervertebral-disc and bone masks are used to define anatomical regions. Contours, bounding boxes, centre points, and connected-component analysis are used to divide the spinal image into separate anatomical regions. Each region is then compared with the lesion mask. A region without lesion overlap is assigned a score of 0; a region containing lesion involvement is assigned a score of 1; and a region containing multiple separate lesion components is assigned a score of 2. Regional scores are subsequently aggregated by image to generate the first-stage SPARCC-related score.'
              + '\n\nThe second stage assesses lesion depth. Intervertebral-disc contours are used to estimate the central positions between adjacent vertebral levels and to construct reference lines perpendicular to the spinal direction. The mean spacing between adjacent anatomical reference points is calculated. Connected-component centroids and the spatial relationship between each lesion and the reference lines are then used to determine whether a lesion fulfils the depth criterion, which is recorded as a binary feature.'
              + '\n\nAn additional intensity-analysis procedure is used to assess lesion intensity. Lesion measurements are first grouped by image identifier, and the maximum lesion intensity for each image is obtained. This value is then compared with a corresponding reference intensity. According to this comparison, the intensity criterion is converted into a binary value of 0 or 1 for subsequent incorporation into SPARCC-related scoring.'
              + '\n\nOverall, the framework forms an automated pipeline consisting of deep-learning lesion segmentation, segmentation performance assessment, anatomical region partitioning, lesion location and multiplicity analysis, lesion-depth assessment, and lesion-intensity assessment. The aim is to convert MRI lesion segmentation outputs into clinically interpretable quantitative features relevant to automated SPARCC assessment.',
          },
        },
        contact: {
          title: 'Contact Us',
          getInTouch: 'Get in Touch',
          description: 'Have questions about our platform or need support? We\'re here to help healthcare professionals make the most of our AI-powered analysis tools.',
          email: {
            title: 'Email Support',
            description: 'For technical support and general inquiries',
            ariaLabel: 'Send email to support team'
          },
          business: {
            title: 'Business Inquiries',
            description: 'For partnerships and enterprise solutions',
            ariaLabel: 'Send email for business inquiries'
          },
          research: {
            title: 'Research Collaboration',
            description: 'For academic partnerships and research projects',
            ariaLabel: 'Send email for research collaboration'
          },
          company: {
            title: 'SPARCC Platform',
            type: 'Medical AI Technology Company',
            location: 'Serving healthcare professionals worldwide',
            availability: '24/7 Platform Availability'
          }
        }
      },
      navigation: {
        homepage: 'Homepage',
        analysisApp: 'Analysis Platform',
        upload: 'Upload',
        query: 'Query'
      },
      home: {
        loadingText: 'System is starting...',
        totalAnalyses: 'Total Analyses',
        todayAnalyses: 'Today',
        avgTime: 'Avg. Processing Time',
        fullScan: 'Complete Scan',
        fullScanDesc: 'Upload single NII/NII.GZ/DCM/DICOM or multiple continuous JPG images of the same patient',
        partialScan: 'Incomplete Scan',
        partialScanDesc: 'Non-continuous images; single or multiple persons supported',
        queryRecords: 'Query Records',
        queryRecordsDesc: 'Enter ID to search history',
        // Additional keys for statistics
        totalAnalysesLabel: 'Total Analyses',
        todayAnalysesLabel: 'Today\'s Analyses',
        monthAnalysesLabel: 'Month\'s Analyses',
        systemStatistics: 'System Statistics',
        uploadMedicalImage: 'Upload medical image for AI analysis',
        batchUploadMedicalImage: 'Batch upload medical images for AI analysis',
        queryHistoryRecords: 'Enter ID to query historical analysis records'
      },
      upload: {
        titleFull: 'Complete Scan Analysis',
        titlePartial: 'Incomplete Scan Analysis',
        subtitleFull: 'Upload one medical image (NII/NII.GZ/DCM/DICOM) or multiple JPGs',
        subtitlePartial: 'Upload JPG or DCM/DICOM files',
        tip: 'Note: Your uploaded images may be used for further research and development.',
        dataConfidentiality: 'All data you upload will be kept strictly confidential and used solely for the intended processing purpose.',
        chooseFiles: 'Choose Files',
        selectedCount: 'Selected {{count}} files',
        addMore: 'Add More',
        start: 'Start',
        uploading: 'Uploading...',
        placeholderTitle: 'Drag files here or click to select',
        placeholderTipFull: 'Support one NII/NII.GZ (≤150MB), DCM/DICOM (≤50MB), or multiple JPG (≤10MB each)',
        completeToast: 'Batch completed! Success: {{success}}, Failed: {{failed}}',
        uploadingBusy: 'Uploading in progress, please wait...',
        selectFormats: 'Please select supported formats: JPG, NII/NII.GZ, DCM, DICOM',
        selectRule: 'Select ONE NII/NII.GZ file, MULTIPLE DCM/DICOM files, or MULTIPLE JPGs. Mixing is not allowed.',
        onlyOneMedical: 'Only one medical image (NII/NII.GZ/DCM/DICOM) is allowed',
        onlyOneNII: 'Only one NII/NII.GZ file is allowed',
        tooManyMedicalFiles: 'Maximum {{max}} medical image files allowed',
        fileTooLarge: 'File {{file}} exceeds the size limit (max {{size}}MB)',
        tooManyImages: 'In incomplete scan mode, the number of images uploaded at once cannot exceed {{max}}, currently selected {{count}}',
        heicNotSupported: 'HEIC images are not supported. Please export as JPG and upload again.',
        pleaseSelect: 'Please select files',
        failed: 'Batch upload failed, please try again',
        // Page-specific / action-related messages
        analysisMissing: 'Analysis result not found, cannot save patient info',
        patientRequired: 'Patient name and ID card are required',
        patientUpdateSuccess: 'Patient info updated successfully',
        patientUpdateFail: 'Failed to save patient info, please try again',
        partialUploadText: 'Upload JPG images',
        fullUploadText: 'Upload medical image or images',
        partialFileTypes: 'Supports JPG (max 10MB each), DCM/DICOM (max 50MB each)',
        fullFileTypes: 'Support NII/NII.GZ/DCM/DICOM or JPG, see size limits',
        startAnalyze: 'Start Analysis',
        reset: 'Reset',
        saveInfo: 'Save Info',
        backUpload: 'Back to Upload',
        // Additional messages
        analysisResultNotFound: 'Analysis result not found, cannot save patient info',
        patientNameIdRequired: 'Patient name and ID card are required',
        patientInfoUpdateSuccess: 'Patient info updated successfully',
        patientInfoUpdateFail: 'Failed to save patient info',
        analysisFailed: 'Analysis failed: {{error}}',
        analysisException: 'Analysis returned exception',
        networkRecovered: 'Network recovered, you can retry saving',
        retryWithCount: 'Click retry button to try again',
      },
      selector: {
        selectMultipleTitle: 'Select multiple medical images',
        selectMultipleDesc: 'Click to choose files or drag them here',
        dataUsageTitle: 'Data Usage Notice',
        dataUsageText: 'Note: Your uploaded images may be used for AI training to improve accuracy',
        formats: 'Supported formats: JPG, WebP, NII/NII.GZ',
        maxFilesInfo: 'Up to {{maxFiles}} files, max size {{maxSize}}MB each',
        selectedLabel: 'Selected:',
        totalSizeLabel: 'Total Size:',
        clearAll: 'Clear All',
        // Additional messages
        estimatedTime: 'Estimated processing time: {{time}} minutes',
        recommendedBatchSize: 'Recommended batch size: {{size}} files',
        suggestBatchProcessing: 'Suggest batch processing',
        validationFailed: 'Validation failed',
        hasWarnings: 'Has warnings',
        validationPassed: 'Validation passed',
        chooseFiles: 'Choose Files',
        uploading: 'Uploading...',
        analyzing: 'Analyzing...',
        completed: 'Completed',
        failed: 'Failed',
        // Additional keys
        deleteFile: 'Delete file',
        captureImage: 'Capture image with camera',
        unknownError: 'Unknown error'
      },
      query: {
        title: 'Medical Imaging Query',
        placeholder: 'Search patient name, ID number, doctor ID or doctor name...',
        searching: 'Searching database...',
        btnSearch: 'Search',
        noResults: 'No records found',
        trySearching: 'Try searching by patient name or doctor name',
        retrySearch: 'Search Again',
        foundCount: 'Found {{count}} sessions',
        totalCount: 'Total {{count}} records',
        prev: 'Prev',
        next: 'Next',
        pageInfo: '{{current}}/{{total}}',
        doctor: 'Doctor',
        images: '{{count}} images',
        completed: 'Completed',
        processing: 'Processing',
        failed: 'Failed',
        high: 'High',
        normal: 'Medium',
        low: 'Low',
        // additional keys used in UI
        searchFail: 'Search failed, please try again',
        loadMoreFail: 'Failed to load more results',
        yesterday: 'yesterday',
        daysAgo: ' days ago',
        weeksAgo: ' weeks ago',
        unknownTime: 'unknown time',
        summary: 'Summary',
        detail: 'Detail',
        unknownPatient: 'Unknown patient',
        systemDoctor: 'System doctor',
        time: 'Time',
        sparccScore: 'SPARCC Score:',
        // Tab labels
        tabAll: 'All',
        tabToday: 'Today',
        tabWeek: 'Week',
        tabMonth: 'Month',
        today: 'Today',
        // Loading and empty states
        loadingSearch: 'Searching...',
        noSearchResults: 'No search results',
        trySearchTip: 'Try searching by patient name or doctor name',
        loadMore: 'Load More',
        loadingMore: 'Loading...',
        search: 'Search'
      },
      progress: {
        title: 'Batch Upload Progress',
        sec: 's',
        min: 'm',
        minute: 'min',
        hour: 'h',
        pending: 'Pending',
        preparing: 'Preparing',
        uploading: 'Uploading',
        analyzing: 'Analyzing',
        completed: 'Completed',
        failed: 'Failed',
        cancelled: 'Cancelled',
        unknown: 'Unknown',
        phasePreparing: 'Preparing files...',
        phaseUploading1: 'Uploading files to server...',
        phaseUploading2: 'Uploading...',
        phaseUploading3: 'Finalizing upload...',
        phaseAnalyzing1: 'Preprocessing images...',
        phaseAnalyzing2: 'AI analyzing images...',
        phaseAnalyzing3: 'Generating report...',
        phaseAnalyzing4: 'Organizing results...',
        completeAll: 'All files completed!',
        error: 'An error occurred',
        cancelledMsg: 'Operation cancelled',
        processing: 'Processing...',
        overall: 'Overall: {{done}}/{{total}}',
        status: 'Status:',
        current: 'Current:',
        remaining: 'Remaining: {{time}}',
        inProgress: 'In progress',
        successRate: 'Success Rate',
        retryFailed: 'Retry failed files ({{count}})',
        showAll: 'Show All',
        onlyFailed: 'Failed Only',
        fileDetails: 'File Details ({{count}})',
        uploadProgress: 'Upload:',
        analysisStart: 'Start:',
        analysisEnd: 'End:',
        errorInfo: 'Error:',
        retry: 'Retry',
        viewResult: 'View',
        noFailed: 'No failed files',
        noFiles: 'No files',
        // Additional messages
        retryingFailedFiles: 'Retrying {{count}} failed files',
        retryingFile: 'Retrying file: {{filename}}',
        // Additional keys
        unknownError: 'Unknown error',
        cancelUpload: 'Cancel upload'
      },
      results: {
        title: 'Batch Results',
        countLine: 'Total {{total}} files, showing {{shown}} results',
        success: 'Success',
        save: 'Save Results',
        avgConfidence: 'Avg Confidence',
        totalLesions: 'Total Lesions',
        noImage: 'No Image',
        detail: 'Analysis Detail',
        detection: 'Detection',
        mask: 'Mask',
        risk: 'Risk',
        basic: 'Basic Info',
        filename: 'Filename:',
        confidence: 'Confidence:',
        riskLevel: 'Risk Level:',
        lesionAnalysis: 'Lesion Analysis',
        lesionCount: 'Lesion Count:',
        totalLesionArea: 'Total Lesion Area:',
        lesionPercent: 'Lesion Percentage:',
        riskAssessment: 'Risk Assessment',
        riskScore: 'Risk Score:',
        riskFactors: 'Risk Factors:',
        recommendation: 'Recommendation:',
        patientInfo: 'Patient Info',
        patientName: 'Name',
        patientId: 'ID',
        patientAge: 'Age',
        patientGender: 'Gender',
        patientPhone: 'Phone',
        pleaseSelect: 'Please select',
        male: 'Male',
        female: 'Female',
        doctorInfo: 'Doctor Info',
        doctorName: 'Doctor Name',
        doctorId: 'Doctor ID (HK)',
        department: 'Department',
        saveHint: 'It will apply patient and doctor info to {{count}} results.',
        saving: 'Saving...',
        empty: 'No matching results',
        tryAdjust: 'Try adjusting filters',
        // Additional messages
        patientDoctorRequired: 'Please fill in patient name, doctor name and doctor ID card',
        analysisRecordNotFound: 'Analysis record not found, please re-analyze',
        patientInfoUpdateSuccess: 'Patient info updated successfully',
        updateFailed: 'Update failed: {{error}}'
      },
      patient: {
        title: 'Add Patient Information',
        desc: 'Please fill in basic patient info to get more accurate suggestions',
        networkOffline: 'Network offline, please retry after reconnecting',
        name: 'Patient Name',
        id: 'ID Card',
        age: 'Age',
        gender: 'Gender',
        male: 'Male',
        female: 'Female',
        phone: 'Phone',
        doctorName: 'Doctor Name',
        doctorId: 'Doctor ID Card',
        notes: 'Notes',
        cancel: 'Cancel',
        retry: 'Retry ({{count}}/3)',
        saving: 'Saving...',
        save: 'Save',
        saveSuccess: 'Saved successfully',
        saveFail: 'Save failed, please retry',
        pleaseSelectGender: 'Select gender',
        // Additional messages
        networkRecovered: 'Network recovered, you can retry saving',
        retryHint: 'Click retry button to try again'
      },
      pwa: {
        title: 'Get a better experience',
        desc: 'Add SPARCC AI to Home Screen for:',
        benefit1: 'Hidden browser address bar',
        benefit2: 'Faster startup',
        benefit3: 'Native-like experience',
        benefit4: 'Offline support',
        iosGuide: 'iOS Install Steps:',
        iosStep1: 'Tap the Share button at bottom',
        iosStep2: 'Choose "Add to Home Screen"',
        iosStep3: 'Tap "Add" to finish',
        installNow: 'Install Now',
        howTo: 'How to Install',
        later: 'Remind me later'
      },
      batch: {
        // Batch patient info manager messages
        templateApplied: 'Template applied: {{name}}',
        fillPatientInfoFirst: 'Please fill in patient info first',
        templateSaved: 'Template "{{name}}" saved successfully',
        clearedAllPatientInfo: 'Cleared all patient info',
        copiedFirstPatientInfo: 'Copied first file patient info to other files',
        firstFilePatientInfoEmpty: 'First file patient info is empty',
        generatedSamplePatientInfo: 'Generated sample patient info',
        fixValidationErrors: 'Please fix validation errors before saving',
        // Additional validation messages
        regularCheckTemplate: 'Regular Check Template',
        pleaseEnterPatientName: 'Please enter patient name',
        nameTooShort: 'Name must be at least 2 characters',
        pleaseEnterIdCard: 'Please enter ID card number',
        invalidIdCardFormat: 'Invalid ID card format (supports Mainland China or Hong Kong ID format)',
        invalidAge: 'Please enter valid age (0-150 years)',
        invalidPhone: 'Please enter valid phone number',
        pleaseEnterDoctorName: 'Please enter doctor name',
        doctorNameTooShort: 'Doctor name must be at least 2 characters',
        pleaseEnterDoctorIdCard: 'Please enter doctor ID card number',
        invalidDoctorIdCardFormat: 'Invalid doctor ID card format (supports Mainland China or Hong Kong ID format)',
        notesTooLong: 'Notes cannot exceed 200 characters'
      },
      analysis: {
        // Analysis detail modal messages
        getDetailFailed: 'Failed to get analysis details',
        unknown: 'Unknown',
        result: 'Analysis result',
        imageLoadFailed: 'Image load failed',
        loadingImage: 'Loading image',
        firstImage: 'First image',
        previousImage: 'Previous image',
        nextImage: 'Next image',
        lastImage: 'Last image',
        startAutoPlay: 'Start auto play',
        stopAutoPlay: 'Stop auto play'
      },
      test: {
        // Test scenario names and descriptions
        normalScenario: 'Normal Scenario Test',
        normalScenarioDesc: 'Select 1-3 image files under 10MB',
        fileCountLimit: 'File Count Limit Test',
        fileCountLimitDesc: 'Maximum 3 files allowed',
        fileSizeLimit: 'File Size Limit Test',
        fileSizeLimitDesc: 'Single file max 2MB, total max 10MB',
        strictLimit: 'Strict Limit Test',
        strictLimitDesc: 'Max 2 files, single max 1MB, total max 2MB'
      },
      spine: {
        title: 'Spine Analysis',
        analysisResults: 'Analysis Results',
        totalSlices: 'Total Slices',
        positiveSlices: 'Positive Slices',
        positiveRate: 'Positive Rate',
        sparccScore: 'SPARCC Score',
        analysisStatus: 'Analysis Status',
        processingTime: 'Processing Time',
        noResults: 'No analysis results',
        noImages: 'No visualization images',
        firstImage: 'First',
        lastImage: 'Last',
        previousImage: 'Previous',
        nextImage: 'Next',
        play: 'Play',
        pause: 'Pause',
        reset: 'Reset',
        close: 'Close',
        analysisImage: 'Analysis Image',
        slice: 'Slice',
        sliceNumber: 'Slice',
        sparccScoreLabel: 'SPARCC Score',
        status: 'Status',
        normal: 'Normal',
        overviewAnalysis: 'Overview Analysis',
        overallAnalysisResults: 'Overall analysis results overview',
        bestConsecutiveSlices: 'Best Consecutive Slices',
        mostRepresentativeSlices: 'Most representative consecutive slice analysis',
        enhancedVisualization: 'Enhanced Visualization',
        aiEnhancedImage: 'AI-enhanced processed image',
        basicVisualization: 'Basic Visualization',
        basicAnalysisResults: 'Basic analysis results',
        detectionResults: 'Detection Results',
        lesionDetectionAnnotation: 'Lesion detection annotation'
      },
      si: {
        title: 'SI Analysis',
        analysisResults: 'Analysis Results',
        totalScore: 'Total SPARCC Score',
        leftScore: 'Left Joint Score',
        rightScore: 'Right Joint Score',
        intensityScore: 'Intensity Score',
        depthScore: 'Depth Score',
        analysisStatus: 'Analysis Status',
        processingTime: 'Processing Time',
        noResults: 'No analysis results',
        noImages: 'No visualization images',
        firstImage: 'First',
        lastImage: 'Last',
        previousImage: 'Previous',
        nextImage: 'Next',
        play: 'Play',
        pause: 'Pause',
        reset: 'Reset',
        close: 'Close',
        analysisImage: 'Analysis Image',
        status: 'Status',
        normal: 'Normal',
        overviewAnalysis: 'Overview Analysis',
        overallAnalysisResults: 'Overall analysis results overview',
        enhancedVisualization: 'Enhanced Visualization',
        aiEnhancedImage: 'AI-enhanced processed image',
        basicVisualization: 'Basic Visualization',
        basicAnalysisResults: 'Basic analysis results',
        detectionResults: 'Detection Results',
        lesionDetectionAnnotation: 'Lesion detection annotation'
      },
      feedback: {
        title: 'User Feedback',
        sectionTitle: 'User Suggestions',
        description: 'Please write your feedback about the analysis result to help us improve the system',
        userSuggestion: 'User Suggestion',
        noduleDetectionRate: 'Nodule Detection Rate',
        scoringAccuracy: 'Scoring Accuracy',
        comments: 'Comments',
        commentsPlaceholder: 'Please provide your suggestions and feedback...',
        satisfied: 'Satisfied',
        average: 'Average',
        unsatisfied: 'Unsatisfied',
        submit: 'Submit Feedback',
        submitting: 'Submitting...',
        submitSuccess: 'Feedback submitted successfully!',
        submitError: 'Failed to submit feedback, please try again',
        required: 'This field is required',
        commentsMaxLength: 'Comments cannot exceed 300 characters',
        networkError: 'Network error, please check your connection',
        validationError: 'Please fill in all required fields',
        thankYou: 'Thank you for your feedback!'
      }
    }
  },
  zh: {
    translation: {
      app: {
        title: 'SPARCC',
        subtitle: '人工智慧醫學影像分析系統',
      },
      common: {
        loading: '系統啟動中...',
        toastLoadDetailFail: '載入詳細資訊失敗',
        confirm: '確定',
        cancel: '取消',
        close: '關閉',
        back: '返回',
        aiDisclaimer: '該AI分析結果僅用於參考目的',
        analysisInProgress: '分析正在進行中，請稍後...',
        fillRequiredFields: '請填寫必填項',
        loadFailed: '載入失敗',
        patientNameRequired: '患者姓名是必填項',
        doctorNameRequired: '醫生姓名是必填項',
        doctorIdRequired: '醫生工號是必填項'
      },
      language: {
        en: 'EN',
        zh: '繁體中文',
        'zh-CN': '简体中文'
      },
      homepage: {
        nav: {
          productInfo: '產品資訊',
          contact: '聯繫我們',
        },
        hero: {
          title: 'SPARCC',
          subtitle: '成為風濕科醫師的 AI 副腦',
          description: 'Rheumatology Limited 致力於透過數位化技術提升風濕病診療的效率與精準度，讓智慧技術深度融入臨床診療流程，為醫師提供強有力的技術支持。',
          downloadMac: 'macOS 下載',
          downloadWin: 'Windows 下載',
          downloadHarmony: '鴻蒙下載',
          downloadHint: '支持 macOS 12+ / Windows 10+ / HarmonyOS 4+',
          learnMore: '了解更多',
          ctaDescription: '下載 SPARCC 桌面應用程式以進行醫學影像分析',
          successRate: '成功率'
        },
        features: {
          title: '平台功能',
          spine: {
            title: '脊椎分析',
            description: '全面的脊椎影像分析與SPARCC評分，提供精確的炎症評估'
          },
          si: {
            title: '薦髂關節分析',
            description: '薦髂關節炎症評估，採用精確的象限評分系統'
          },
          analytics: {
            title: '進階分析',
            description: '詳細報告、統計分析與完整的患者病史追蹤'
          },
          ai: {
            title: 'AI智能檢測',
            description: '最先進的人工智慧技術，提供精確的病變檢測與分類'
          },
          speed: {
            title: '快速處理',
            description: '快速分析處理，通常在上傳後數分鐘內即可獲得結果'
          },
          security: {
            title: '安全合規',
            description: '符合HIPAA標準的資料處理，採用企業級安全措施'
          }
        },
        about: {
          title: '關於 SPARCC 平台',
          description: 'SPARCC（加拿大脊椎關節炎研究聯盟）是領先的醫學影像分析平台，運用先進的人工智慧技術，為脊椎和薦髂關節炎症提供準確且高效的評估。我們的平台旨在協助醫療專業人員做出明智的診斷決策。',
          highlightsTitle: '主要特色',
          highlight1: '受到全球醫療專業人員信賴',
          highlight2: '經過臨床驗證的研究支持AI演算法',
          highlight3: '快速分析，數分鐘內獲得結果',
          highlight4: '企業級安全性和HIPAA合規性',
          missionTitle: '我們的使命',
          mission: '透過為醫療專業人員提供尖端的AI工具來革新醫學影像分析，提高診斷準確性，縮短分析時間，最終改善風濕病學和脊椎護理領域的患者治療效果。'
        },
        visionMission: {
          title: '公司願景與使命',
          subtitle: '成為風濕科醫師的 AI 副腦',
          step1: {
            title: '公司使命',
            description: 'Rheumatology Limited 致力於透過數位化技術提升風濕病診療的效率與精準度，讓智慧技術深度融入臨床診療流程，為醫師提供強有力的技術支持。'
          },
          step2: {
            title: '產品矩陣',
            description: '公司打造了 RheumaScore™ 影像 AI 評分家族，涵蓋 SPARCC Spine 與 SI Joint，為風濕科與影像科醫師提供即時、精準、快速的決策支援工具。'
          },
          step3: {
            title: '未來目標',
            description: '我們的最終目標是成為風濕科與影像科醫師的 AI 副腦，推動風濕病診療的智慧化進程，提升診療效率並改善患者預後。'
          }
        },
        painPoints: {
          title: '痛點洞察與解方',
          sparcc: {
            title: 'SPARCC 評分系統',
            step1: '強直性脊柱炎',
            step2: '慢性風濕類疾病',
            step3: '引發炎症',
            assessment: '炎症評估',
            grading: '評估骶髂關節與脊柱的炎症程度',
            scoring: '在STIR-MRI上運用SPARCC評分系統',
            note: 'SPARCC 評分系統是最常用的'
          },
          traditional: {
            title: '傳統 SPARCC 評分兩大痛點',
            inefficiency: {
              title: '效率低落',
              description: '傳統人工 SPARCC 評分平均耗時 8–12 分鐘，難以滿足快速診斷的需求，影響臨床效率與患者體驗。'
            },
            reliability: {
              title: '信度不足',
              description: '多中心研究顯示，人工評分的組間一致性 κ 僅為 0.62，評分差異大，影響臨床決策的準確性。'
            }
          },
        ai: {
          title: '核心功能特點',
            automation: {
              title: '自動化評分',
              description: 'AI 系統可一鍵上傳 MRI-STIR 影像，自動分割脊柱節段與雙側骶髂關節，實現全程自動化評分。'
            },
            efficiency: {
              title: '高效評分',
              description: 'AI 系統在幾秒內完成評分，並生成熱圖與結構化報告，大幅提升評分效率。'
            },
            precision: {
              title: '高精度',
              description: 'AI 評分結果與風濕科及影像科專家評分的 ICC 高達 0.80（脊柱）及 0.83（骶髂關節），確保評分的高精度與可靠性。'
            },
            objective: {
              title: '客觀可重複',
              description: 'AI 系統提供即時、客觀、可重複的評分結果，為臨床決策提供可靠的數據支持。'
            }
          }
        },
        productShowcase: {
          title: '產品功能展示',
          subtitle: '體驗SPARCC AI醫學影像分析平台的強大功能',
          tabs: {
            spine: '脊柱分析',
            spineDesc: 'SPARCC脊柱炎症評估',
            si: 'SI關節分析',
            siDesc: '骶髂關節炎症評分',
            system: '系統界面',
            systemDesc: '平台操作界面展示'
          },
          images: {
            spine1: {
              alt: '脊柱分析結果1',
              title: '脊柱分割與評分',
              description: 'AI自動分割脊柱節段，提供精確的SPARCC評分'
            },
            spine2: {
              alt: '脊柱分析結果2',
              title: '脊柱炎症熱圖',
              description: '可視化炎症區域，輔助臨床診斷決策'
            },
            si: {
              alt: 'SI關節分析結果',
              title: '骶髂關節評分',
              description: '四象限評分系統，精確評估骶髂關節炎症程度'
            },
            sys1: {
              alt: '系統界面1',
              title: '分析統計界面',
              description: '實時分析統計，支持多語言切換'
            },
            sys2: {
              alt: '系統界面2',
              title: '上傳分析界面',
              description: '支持多種醫學影像格式，一鍵上傳分析'
            }
          },
          highlights: {
            title: '核心功能特點',
            speed: {
              title: '快速分析',
              description: '3秒內完成SPARCC評分，大幅提升診斷效率'
            },
            accuracy: {
              title: '高精度評分',
              description: 'ICC高達0.80-0.83，與專家評分高度一致'
            },
            automation: {
              title: '全自動化',
              description: '一鍵上傳，自動分割，無需人工干預'
            },
            visualization: {
              title: '可視化報告',
              description: '熱圖顯示炎症區域，結構化報告便於臨床決策'
            }
          }
        },
        publication: {
          title: '學術發表',
          subtitle: '基於SPARCC評分系統的深度學習算法研究成果',
          authors: '作者',
          published: '已發表',
          pub1: {
            title: '基於深度學習的骶髂關節MRI SPARCC評分系統算法研究',
            journal: 'Journal of Magnetic Resonance Imaging'
          },
          pub2: {
            title: '中軸型脊柱關節炎MRI脊柱炎症的深度神經網絡研究',
            journal: 'European spine journal'
          },
          pub3: {
            title: '中軸型脊柱關節炎炎症性骶髂關節炎磁共振成像的深度學習算法',
            journal: 'Rheumatology'
          },
          pub4: {
            title: '基於深度學習的中軸型脊柱關節炎脊柱炎症半定量評估算法',
            journal: 'RMD Open'
          },
          highlight: {
            title: '研究亮點',
            papers: '篇論文',
            period: '發表時間'
          }
        },
        productInfo: {
          title: '產品資訊',
          subtitle: '我們 AI 醫學影像平台的監管披露資訊',
          relatedNotes: {
            title: '相關說明',
            intro: '本平台面向影像科（放射科）與風濕免疫科醫生。AI 用於輔助評估脊骨及骶髂關節的發炎情況，並自動計算 SPARCC 炎症評分。',
            warning: 'AI 分析結果僅供醫療專業人員參考，不能替代醫師的正式臨床診斷。',
            validation: '演算法有效性已通過同行評審臨床研究驗證，研究列表見下方。',
          },
          algorithmTraining: {
            label: '演算法訓練摘要',
            brief: '基於 U-Net 神經網絡訓練病灶分割模型，訓練方法詳見右側摘要。',
            body: 'AI 模型基於 U-Net 卷積神經網絡，用於分割脊椎 MRI 影像中的病灶。訓練時，連續三張切片合併為一幅三通道圖像，統一縮放至 128 × 128 像素；損失函數由 Dice 係數和加權二元交叉熵組成，並加大病灶像素的權重，避免因病灶區域太小而漏檢；預測結果再經 0.5 閾值過濾，去掉信心度較低的部分。',
            fullSummaryLabel: '查看完整摘要',
            fullText: '本研究建立了一套結合深度學習影像分割及規則式影像分析的自動化演算法，用於辨識脊椎磁力共振影像中的病灶，並進一步計算與 SPARCC 評分相關的影像特徵。'
              + '\n\n首先，病灶分割部分採用已訓練的 U-Net 深度學習模型。輸入影像會調整至 128 × 128 像素，並把相連三張影像分別放入不同的彩色三通道內，從而組成三通道影像，並配合相應的病灶標註遮罩進行模型推論及效能評估。模型相關設定包括 Dice 相似係數（Dice coefficient）、加權二元交叉熵（weighted binary cross-entropy），以及由兩者組成的自訂損失函數。當中病灶像素會被賦予較高權重，以減低背景與病灶像素數量不平衡所造成的影響。模型輸出其後會以 0.5 作為閾值進行後處理，以移除信心度較低的預測像素。'
              + '\n\n模型表現主要透過 Dice 相似係數評估病灶分割結果，並進一步利用混淆矩陣（confusion matrix）計算真正例、真反例、假正例及假反例。系統亦會建立受試者工作特徵曲線（receiver operating characteristic curve，ROC curve），並計算曲線下面積（area under the curve，AUC），用以評估演算法區分有病灶及無病灶影像的能力。此外，程式亦會比較真實標註遮罩與預測遮罩內各個獨立病灶輪廓的重疊程度，以分析病灶層級的偵測表現。'
              + '\n\n完成病灶分割後，系統會再透過影像處理演算法進行 SPARCC 相關評分。第一階段利用椎間盤遮罩及骨骼遮罩找出不同脊椎解剖區域，並透過輪廓、外接矩形、中心點及連通元件分析，將影像劃分成不同區域。其後，每個解剖區域會與病灶遮罩進行重疊分析：若區域內沒有病灶重疊，記為 0 分；若存在病灶重疊，記為 1 分；若同一區域內出現多個獨立病灶連通元件，則記為 2 分。各區域分數最後會按影像名稱合併，以形成每張影像的第一階段 SPARCC 相關分數。'
              + '\n\n第二階段主要分析病灶深度。演算法會根據椎間盤輪廓計算相鄰椎體之間的中心位置，並建立與脊椎方向垂直的參考線。同時，系統亦會計算相鄰解剖參考點之間的平均距離。其後，再根據病灶連通元件的質心位置，以及病灶與參考線之間的空間關係，判斷病灶是否符合深部病灶的條件，並以 0 或 1 的二元方式記錄深度特徵。'
              + '\n\n最後，演算法亦加入病灶強度比較。系統會先根據影像名稱整合病灶區域的強度資料，並取得每張影像中病灶的最大強度值，再與相對應的參考強度值進行比較。根據兩者的相對大小，系統會將強度條件轉換為 0 或 1 的二元結果，作為後續 SPARCC 相關評分的一部分。'
              + '\n\n整體而言，此方法形成了一個由「深度學習病灶分割、分割效能評估、解剖區域劃分、病灶位置及數量分析、病灶深度分析，以及病灶強度分析」所組成的自動化流程，目標是將磁力共振影像中的病灶分割結果，進一步轉化為具臨床意義的 SPARCC 相關量化指標。',
          },
        },
        contact: {
          title: '聯絡我們',
          getInTouch: '與我們聯繫',
          description: '對我們的平台有疑問或需要支援？我們在此協助醫療專業人員充分利用我們的AI分析工具。',
          email: {
            title: '電子郵件支援',
            description: '技術支援和一般查詢',
            ariaLabel: '發送電子郵件給支援團隊'
          },
          business: {
            title: '商業查詢',
            description: '合作夥伴關係和企業解決方案',
            ariaLabel: '發送商業查詢電子郵件'
          },
          research: {
            title: '研究合作',
            description: '學術合作夥伴關係和研究項目',
            ariaLabel: '發送研究合作電子郵件'
          },
          company: {
            title: 'SPARCC 平台',
            type: '醫學AI技術公司',
            location: '為全球醫療專業人員提供服務',
            availability: '24/7 平台可用性'
          }
        }
      },
      navigation: {
        backToHomepage: '返回首頁',
        homepage: '首頁',
        analysisApp: '分析平台',
        upload: '上傳',
        query: '查詢'
      },
      home: {
        loadingText: '系統啟動中...',
        totalAnalyses: '總分析次數',
        todayAnalyses: '今日分析',
        avgTime: '平均處理時間',
        fullScan: '完整掃描',
        fullScanDesc: '上傳單個醫學影像檔案(NII/NII.GZ/DCM/DICOM)或同一患者的連續掃描圖片',
        partialScan: '非完整掃描',
        partialScanDesc: '非連續性掃描圖片分析，可以是單張或多人多張',
        queryRecords: '查詢分析記錄',
        queryRecordsDesc: '輸入身份證號查詢歷史記錄',
        totalAnalysesLabel: '總分析次數',
        todayAnalysesLabel: '今日分析',
        monthAnalysesLabel: '本月分析',
        systemStatistics: '系統統計',
        uploadMedicalImage: '上傳醫學影像進行AI分析',
        batchUploadMedicalImage: '批量上傳醫學影像進行AI分析',
        queryHistoryRecords: '輸入身份證號查詢歷史分析記錄'
      },
      upload: {
        titleFull: '完整掃描分析',
        titlePartial: '非完整掃描分析',
        subtitleFull: '上傳單個醫學影像檔案(NII/NII.GZ/DCM/DICOM)或同一患者的多張JPG掃描圖片',
        subtitlePartial: '上傳JPG或DCM/DICOM檔案進行非連續性掃描分析',
        tip: '注意：上傳的圖片有可能會被用於AI的進一步訓練',
        dataConfidentiality: '您上傳的所有資料都將被嚴格保密，僅用於指定的處理目的。',
        chooseFiles: '選擇檔案',
        selectedCount: '已選擇 {{count}} 個檔案',
        addMore: '新增更多',
        start: '開始分析',
        uploading: '上傳中...',
        placeholderTitle: '拖曳檔案到此處或點擊選擇',
        placeholderTipFull: '支援單個 NII/NII.GZ(最大150MB)、DCM/DICOM(最大50MB)，或多個 JPG 圖片(最大10MB)',
        completeToast: '批量分析完成！成功: {{success}}, 失敗: {{failed}}',
        uploadingBusy: '上傳正在進行中，請稍後...',
        selectFormats: '請選擇支援的檔案格式：JPG、NII/NII.GZ、DCM、DICOM',
        selectRule: '請選擇一個NII/NII.GZ檔案、多個DCM/DICOM檔案或多個JPG圖片，不能混合選擇',
        onlyOneMedical: '醫學影像檔案(NII/NII.GZ/DCM/DICOM)只能選擇一個',
        onlyOneNII: 'NII/NII.GZ檔案只能選擇一個',
        tooManyMedicalFiles: '最多只能選擇 {{max}} 個醫學影像檔案',
        fileTooLarge: '檔案 {{file}} 大小超出限制，最大允許 {{size}}MB',
        tooManyImages: '非完整掃描模式下，單次上傳的圖片數量不能超過 {{max}} 張，當前選擇了 {{count}} 張',
        heicNotSupported: '目前暫不支援 HEIC 圖片，請在相簿中匯出為 JPG 後再上傳',
        pleaseSelect: '請先選擇檔案',
        failed: '批量上傳失敗，請重試',
        analysisMissing: '分析結果未找到，無法儲存患者資訊',
        patientRequired: '患者姓名和身份證號是必填項',
        patientUpdateSuccess: '患者資訊更新成功',
        patientUpdateFail: '儲存患者資訊失敗，請重試',
        partialUploadText: '上傳JPG圖片',
        fullUploadText: '上傳醫學影像或圖片',
        partialFileTypes: '支援JPG（單個最大10MB）、DCM/DICOM（單個最大50MB）',
        fullFileTypes: '支援NII/NII.GZ/DCM/DICOM或JPG，詳見大小限制',
        startAnalyze: '開始分析',
        reset: '重設',
        saveInfo: '儲存資訊',
        backUpload: '返回上傳',
        analysisResultNotFound: '分析結果不存在，無法儲存患者資訊',
        patientNameIdRequired: '患者姓名和身份證號不能為空',
        patientInfoUpdateSuccess: '患者資訊更新成功',
        patientInfoUpdateFail: '儲存患者資訊失敗',
        analysisFailed: '分析失敗: {{error}}',
        analysisException: '分析返回異常',
        networkRecovered: '網路已恢復，可以重試儲存',
        retryWithCount: '，點擊重試按鈕重新嘗試'
      },
      selector: {
        selectMultipleTitle: '選擇多個醫學影像',
        selectMultipleDesc: '點擊選擇檔案或拖曳檔案到此處',
        dataUsageTitle: '資料使用聲明',
        dataUsageText: '注意：您上傳的圖片未來有可能會被用於AI訓練資料，以改進診斷準確性',
        formats: '支援 JPG、WebP、NII/NII.GZ 格式',
        maxFilesInfo: '最多 {{maxFiles}} 個檔案，單個檔案最大 {{maxSize}}MB',
        selectedLabel: '已選擇:',
        totalSizeLabel: '總大小:',
        clearAll: '清空所有',
        estimatedTime: '預計處理時間: {{time}} 分鐘',
        recommendedBatchSize: '推薦批次大小: {{size}} 個檔案',
        suggestBatchProcessing: '建議分批處理',
        validationFailed: '驗證失敗',
        hasWarnings: '有警告',
        validationPassed: '驗證通過',
        chooseFiles: '選擇檔案',
        uploading: '上傳中...',
        analyzing: '分析中...',
        completed: '完成',
        failed: '失敗',
        deleteFile: '刪除檔案',
        captureImage: '使用相機拍攝圖像',
        unknownError: '未知錯誤',
        validationErrors: '驗證錯誤',
        validationWarnings: '驗證警告',
        validatingFiles: '正在驗證檔案...'
      },
      query: {
        title: '醫學影像分析查詢',
        placeholder: '搜尋患者姓名、身份證號、醫生工號或醫生姓名...',
        searching: '正在查詢資料庫...',
        btnSearch: '搜尋',
        noResults: '未找到相關記錄',
        trySearching: '嘗試搜尋患者姓名或醫生姓名',
        retrySearch: '重新搜尋',
        foundCount: '找到 {{count}} 個分析會話',
        totalCount: '共找到 {{count}} 條記錄',
        prev: '上一頁',
        next: '下一頁',
        pageInfo: '{{current}}/{{total}}',
        doctor: '醫生',
        images: '{{count}}張',
        completed: '已完成',
        processing: '處理中',
        failed: '失敗',
        high: '高風險',
        normal: '中風險',
        low: '低風險',
        searchFail: '查詢失敗，請稍後重試',
        loadMoreFail: '載入更多結果失敗',
        yesterday: '昨天',
        daysAgo: ' 天前',
        weeksAgo: ' 週前',
        unknownTime: '未知時間',
        summary: '概要',
        detail: '詳情',
        unknownPatient: '未知患者',
        systemDoctor: '系統醫生',
        time: '時間',
        sparccScore: 'SPARCC 評分:',
        tabAll: '全部',
        tabToday: '今日',
        tabWeek: '本週',
        tabMonth: '本月',
        today: '今天',
        loading: '載入中...',
        loadingSearch: '搜尋中...',
        noSearchResults: '暫無搜尋結果',
        trySearchTip: '嘗試搜尋患者姓名或醫生姓名',
        loadMore: '載入更多',
        loadingMore: '載入中...',
        search: '搜尋'
      },
      progress: {
        title: '批量上傳進度',
        sec: '秒',
        min: '分',
        minute: '分鐘',
        hour: '小時',
        pending: '等待中',
        preparing: '準備中',
        uploading: '上傳中',
        analyzing: '分析中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消',
        unknown: '未知',
        phasePreparing: '正在準備檔案...',
        phaseUploading1: '正在上傳檔案到伺服器...',
        phaseUploading2: '檔案上傳進行中...',
        phaseUploading3: '即將完成檔案上傳...',
        phaseAnalyzing1: '正在預處理圖像...',
        phaseAnalyzing2: 'AI正在分析圖像內容...',
        phaseAnalyzing3: '正在產生分析報告...',
        phaseAnalyzing4: '正在整理分析結果...',
        completeAll: '所有檔案處理完成！',
        error: '處理過程中出現錯誤',
        cancelledMsg: '操作已被取消',
        processing: '正在處理...',
        overall: '整體進度: {{done}}/{{total}}',
        status: '狀態:',
        current: '當前:',
        remaining: '預計剩餘: {{time}}',
        inProgress: '進行中',
        successRate: '成功率',
        retryFailed: '重試失敗檔案 ({{count}})',
        showAll: '顯示全部',
        onlyFailed: '僅顯示失敗',
        fileDetails: '檔案詳情 ({{count}})',
        uploadProgress: '上傳進度:',
        analysisStart: '分析開始:',
        analysisEnd: '分析完成:',
        errorInfo: '錯誤資訊:',
        retry: '重試',
        viewResult: '查看結果',
        noFailed: '沒有失敗的檔案',
        noFiles: '沒有檔案',
        retryingFailedFiles: '正在重試 {{count}} 個失敗的檔案',
        retryingFile: '正在重試檔案: {{filename}}',
        unknownError: '未知錯誤',
        cancelUpload: '取消上傳'
      },
      results: {
        title: '批量分析結果',
        countLine: '共 {{total}} 個檔案，顯示 {{shown}} 個結果',
        success: '成功分析',
        save: '儲存結果',
        avgConfidence: '平均信賴度',
        totalLesions: '總病變數',
        noImage: '暫無圖像',
        detail: '分析詳情',
        detection: '檢測圖',
        mask: '遮罩',
        risk: '風險圖',
        basic: '基本資訊',
        filename: '檔案名稱:',
        confidence: '信賴度:',
        riskLevel: '風險等級:',
        lesionAnalysis: '病變分析',
        lesionCount: '病變數量:',
        totalLesionArea: '總病變面積:',
        lesionPercent: '病變百分比:',
        riskAssessment: '風險評估',
        riskScore: '風險評分:',
        riskFactors: '風險因素:',
        recommendation: '建議:',
        patientInfo: '患者資訊',
        patientName: '姓名',
        patientId: '身份證號',
        patientAge: '年齡',
        patientGender: '性別',
        patientPhone: '聯絡電話',
        pleaseSelect: '請選擇',
        male: '男',
        female: '女',
        doctorInfo: '醫生資訊',
        doctorName: '醫生姓名',
        doctorId: '醫生身份證號(香港)',
        department: '科別',
        saveHint: '將為 {{count}} 個分析結果統一新增患者和醫生資訊，儲存後可透過首頁查詢功能查看。所有結果將使用相同的患者和醫生資訊。',
        saving: '儲存中...',
        empty: '沒有找到相符的結果',
        tryAdjust: '嘗試調整篩選條件',
        patientDoctorRequired: '請填寫患者姓名、醫生姓名和醫生身份證號',
        analysisRecordNotFound: '未找到對應的分析記錄，請重新分析',
        patientInfoUpdateSuccess: '患者資訊更新成功',
        updateFailed: '更新失敗: {{error}}'
      },
      patient: {
        title: '📝 補充患者資訊',
        desc: '請填寫患者基本資訊，有助於獲得更準確的診斷建議',
        networkOffline: '⚠️ 網路連線已中斷，請檢查網路後重試',
        name: '患者姓名',
        id: '身份證號',
        age: '年齡',
        gender: '性別',
        male: '男',
        female: '女',
        phone: '聯絡電話',
        doctorName: '醫生姓名',
        doctorId: '醫生身份證號',
        notes: '備註',
        cancel: '取消',
        retry: '重試 ({{count}}/3)',
        saving: '儲存中...',
        save: '儲存資訊',
        saveSuccess: '儲存成功',
        saveFail: '儲存失敗，請重試',
        pleaseSelectGender: '請選擇性別',
        networkRecovered: '網路已恢復，可以重試儲存',
        retryHint: '，點擊重試按鈕重新嘗試'
      },
      pwa: {
        title: '獲得更好的體驗',
        desc: '將 SPARCC AI 新增到主畫面，享受：',
        benefit1: '隱藏瀏覽器網址列',
        benefit2: '更快的啟動速度',
        benefit3: '原生應用體驗',
        benefit4: '離線功能支援',
        iosGuide: 'iOS安裝步驟：',
        iosStep1: '點擊底部 分享 按鈕',
        iosStep2: '選擇「新增到主畫面」',
        iosStep3: '點擊「新增」完成安裝',
        installNow: '立即安裝',
        howTo: '查看安裝方法',
        later: '稍後提醒'
      },
      batch: {
        templateApplied: '已套用範本: {{name}}',
        fillPatientInfoFirst: '請先填寫患者資訊',
        templateSaved: '範本 "{{name}}" 儲存成功',
        clearedAllPatientInfo: '已清空所有患者資訊',
        copiedFirstPatientInfo: '已將第一個檔案的患者資訊複製到其他檔案',
        firstFilePatientInfoEmpty: '第一個檔案的患者資訊為空',
        generatedSamplePatientInfo: '已產生範例患者資訊',
        fixValidationErrors: '請修正驗證錯誤後再儲存',
        regularCheckTemplate: '常規檢查範本',
        pleaseEnterPatientName: '請輸入患者姓名',
        nameTooShort: '姓名必須至少2個字元',
        pleaseEnterIdCard: '請輸入身份證號',
        invalidIdCardFormat: '身份證號格式不正確（支援大陸或香港身份證格式）',
        invalidAge: '請輸入有效年齡（0-150歲）',
        invalidPhone: '請輸入有效手機號碼',
        pleaseEnterDoctorName: '請輸入醫生姓名',
        doctorNameTooShort: '醫生姓名必須至少2個字元',
        pleaseEnterDoctorIdCard: '請輸入醫生身份證號',
        invalidDoctorIdCardFormat: '醫生身份證號格式不正確（支援大陸或香港身份證格式）',
        notesTooLong: '備註不能超過200個字元'
      },
      analysis: {
        getDetailFailed: '取得分析詳情失敗',
        unknown: '未知',
        result: '分析結果',
        imageLoadFailed: '圖片載入失敗',
        loadingImage: '正在載入圖片',
        firstImage: '第一張',
        previousImage: '上一張',
        nextImage: '下一張',
        lastImage: '最後一張',
        startAutoPlay: '開始自動播放',
        stopAutoPlay: '停止自動播放'
      },
      spine: {
        title: 'Spine關節分析',
        analysisResults: '分析結果',
        totalSlices: '總切片數',
        positiveSlices: '陽性切片數',
        positiveRate: '陽性率',
        sparccScore: 'SPARCC評分',
        analysisStatus: '分析狀態',
        processingTime: '處理時間',
        noResults: '暫無分析結果',
        noImages: '暫無可視化圖像',
        firstImage: '第一張',
        lastImage: '最後一張',
        previousImage: '上一張',
        nextImage: '下一張',
        play: '播放',
        pause: '暫停',
        reset: '重設',
        close: '關閉',
        analysisImage: '分析圖像',
        slice: '切片',
        sliceNumber: '切片',
        sparccScoreLabel: 'SPARCC評分',
        status: '狀態',
        normal: '正常',
        overviewAnalysis: '概覽分析',
        overallAnalysisResults: '整體分析結果概覽',
        bestConsecutiveSlices: '最佳連續切片',
        mostRepresentativeSlices: '最具代表性的連續切片分析',
        enhancedVisualization: '增強可視化',
        aiEnhancedImage: 'AI增強處理圖像',
        basicVisualization: '基礎可視化',
        basicAnalysisResults: '基礎分析結果',
        detectionResults: '檢測結果',
        lesionDetectionAnnotation: '病變檢測標註'
      },
      si: {
        title: 'SI關節分析',
        analysisResults: '分析結果',
        totalScore: '總SPARCC評分',
        leftScore: '左側關節評分',
        rightScore: '右側關節評分',
        intensityScore: '強度評分',
        depthScore: '深度評分',
        analysisStatus: '分析狀態',
        processingTime: '處理時間',
        noResults: '暫無分析結果',
        noImages: '暫無可視化圖像',
        firstImage: '第一張',
        lastImage: '最後一張',
        previousImage: '上一張',
        nextImage: '下一張',
        play: '播放',
        pause: '暫停',
        reset: '重設',
        close: '關閉',
        analysisImage: '分析圖像',
        status: '狀態',
        normal: '正常',
        overviewAnalysis: '概覽分析',
        overallAnalysisResults: '整體分析結果概覽',
        enhancedVisualization: '增強可視化',
        aiEnhancedImage: 'AI增強處理圖像',
        basicVisualization: '基礎可視化',
        basicAnalysisResults: '基礎分析結果',
        detectionResults: '檢測結果',
        lesionDetectionAnnotation: '病變檢測標註'
      },
      feedback: {
        title: '用戶回饋',
        sectionTitle: '用戶建議',
        description: '請填寫您對分析結果的回饋，有助於我們改進系統',
        userSuggestion: '用戶建議',
        noduleDetectionRate: '結節探查率',
        scoringAccuracy: '評分準確率',
        comments: '意見',
        commentsPlaceholder: '請提供您的建議和回饋...',
        satisfied: '滿意',
        average: '一般',
        unsatisfied: '不滿意',
        submit: '提交回饋',
        submitting: '提交中...',
        submitSuccess: '回饋提交成功！',
        submitError: '回饋提交失敗，請重試',
        required: '此項為必填項',
        commentsMaxLength: '意見不能超過300個字元',
        networkError: '網路錯誤，請檢查網路連線',
        validationError: '請填寫所有必填項',
        thankYou: '感謝您的回饋！'
      },
      test: {
        normalScenario: '正常場景測試',
        normalScenarioDesc: '選擇1-3個大小不超過10MB的圖片檔案',
        fileCountLimit: '檔案數量限制測試',
        fileCountLimitDesc: '最多允許3個檔案',
        fileSizeLimit: '檔案大小限制測試',
        fileSizeLimitDesc: '單個檔案最大2MB，總和最大10MB',
        strictLimit: '嚴格限制測試',
        strictLimitDesc: '最多2個檔案，單個最大1MB，總和最大2MB'
      }
    }
  },
  'zh-CN': {
    translation: {
      app: {
        title: 'SPARCC',
        subtitle: '人工智能医学影像分析系统',
      },
      spine: {
        title: 'Spine关节检测',
        analysisResults: '分析结果',
        totalSlices: '总切片数',
        positiveSlices: '阳性切片数',
        positiveRate: '阳性率',
        sparccScore: 'SPARCC评分',
        analysisStatus: '分析状态',
        processingTime: '处理时间',
        noResults: '暂无分析结果',
        noImages: '暂无可视化图像',
        firstImage: '第一张',
        lastImage: '最后一张',
        previousImage: '上一张',
        nextImage: '下一张',
        play: '播放',
        pause: '暂停',
        reset: '重置',
        close: '关闭',
        analysisImage: '分析图像',
        slice: '切片',
        sliceNumber: '切片',
        sparccScoreLabel: 'SPARCC评分',
        status: '状态',
        normal: '正常',
        overviewAnalysis: '概览分析',
        overallAnalysisResults: '整体分析结果概览',
        bestConsecutiveSlices: '最佳连续切片',
        mostRepresentativeSlices: '最具代表性的连续切片分析',
        enhancedVisualization: '增强可视化',
        aiEnhancedImage: 'AI增强处理图像',
        basicVisualization: '基础可视化',
        basicAnalysisResults: '基础分析结果',
        detectionResults: '检测结果',
        lesionDetectionAnnotation: '病变检测标注'
      },
      si: {
        title: 'SI关节检测',
        analysisResults: '分析结果',
        totalScore: '总SPARCC评分',
        leftScore: '左侧关节评分',
        rightScore: '右侧关节评分',
        intensityScore: '强度评分',
        depthScore: '深度评分',
        analysisStatus: '分析状态',
        processingTime: '处理时间',
        noResults: '暂无分析结果',
        noImages: '暂无可视化图像',
        firstImage: '第一张',
        lastImage: '最后一张',
        previousImage: '上一张',
        nextImage: '下一张',
        play: '播放',
        pause: '暂停',
        reset: '重置',
        close: '关闭',
        analysisImage: '分析图像',
        status: '状态',
        normal: '正常',
        overviewAnalysis: '概览分析',
        overallAnalysisResults: '整体分析结果概览',
        enhancedVisualization: '增强可视化',
        aiEnhancedImage: 'AI增强处理图像',
        basicVisualization: '基础可视化',
        basicAnalysisResults: '基础分析结果',
        detectionResults: '检测结果',
        lesionDetectionAnnotation: '病变检测标注'
      },
      common: {
        loading: '系统启动中...',
        toastLoadDetailFail: '加载详细信息失败',
        confirm: '确定',
        cancel: '取消',
        close: '关闭',
        back: '返回',
        aiDisclaimer: '该AI分析结果仅用于参考目的',
        analysisInProgress: '分析正在进行中，请稍后...',
        fillRequiredFields: '请填写必填项',
        loadFailed: '加载失败',
        patientNameRequired: '患者姓名是必填项',
        doctorNameRequired: '医生姓名是必填项',
        doctorIdRequired: '医生工号是必填项'
      },
      language: {
        en: 'EN',
        zh: '繁體中文',
        'zh-CN': '简体中文'
      },
      homepage: {
        nav: {
          productInfo: '产品信息',
          contact: '联系我们',
        },
        hero: {
          title: 'SPARCC',
          subtitle: '成为风湿科医师的 AI 副脑',
          description: 'Rheumatology Limited 致力于通过数字化技术提升风湿病诊疗的效率与精准度，让智慧技术深度融入临床诊疗流程，为医师提供强有力的技术支持。',
          downloadMac: 'macOS 下载',
          downloadWin: 'Windows 下载',
          downloadHarmony: '鸿蒙下载',
          downloadHint: '支持 macOS 12+ / Windows 10+ / HarmonyOS 4+',
          learnMore: '了解更多',
          ctaDescription: '下载 SPARCC 桌面应用程序以进行医学影像分析',
          successRate: '成功率'
        },
        features: {
          title: '平台功能',
          spine: {
            title: '脊椎分析',
            description: '全面的脊椎影像分析与SPARCC评分，提供精确的炎症评估'
          },
          si: {
            title: '骶髂关节分析',
            description: '骶髂关节炎症评估，采用精确的象限评分系统'
          },
          analytics: {
            title: '高级分析',
            description: '详细报告、统计分析与完整的患者病史追踪'
          },
          ai: {
            title: 'AI智能检测',
            description: '最先进的人工智能技术，提供精确的病变检测与分类'
          },
          speed: {
            title: '快速处理',
            description: '快速分析处理，通常在上传后数分钟内即可获得结果'
          },
          security: {
            title: '安全合规',
            description: '符合HIPAA标准的数据处理，采用企业级安全措施'
          }
        },
        about: {
          title: '关于 SPARCC 平台',
          description: 'SPARCC（加拿大脊椎关节炎研究联盟）是领先的医学影像分析平台，运用先进的人工智能技术，为脊椎和骶髂关节炎症提供准确且高效的评估。我们的平台旨在协助医疗专业人员做出明智的诊断决策。',
          highlightsTitle: '主要特色',
          highlight1: '受到全球医疗专业人员信赖',
          highlight2: '经过临床验证的研究支持AI算法',
          highlight3: '快速分析，数分钟内获得结果',
          highlight4: '企业级安全性和HIPAA合规性',
          missionTitle: '我们的使命',
          mission: '通过为医疗专业人员提供尖端的AI工具来革新医学影像分析，提高诊断准确性，缩短分析时间，最终改善风湿病学和脊椎护理领域的患者治疗效果。'
        },
        visionMission: {
          title: '公司愿景与使命',
          subtitle: '成为风湿科医师的 AI 副脑',
          step1: {
            title: '公司使命',
            description: 'Rheumatology Limited 致力于通过数字化技术提升风湿病诊疗的效率与精准度，让智慧技术深度融入临床诊疗流程，为医师提供强有力的技术支持。'
          },
          step2: {
            title: '产品矩阵',
            description: '公司打造了 RheumaScore™ 影像 AI 评分家族，涵盖 SPARCC Spine 与 SI Joint，为风湿科与影像科医师提供即时、精准、快速的决策支援工具。'
          },
          step3: {
            title: '未来目标',
            description: '我们的最终目标是成为风湿科与影像科医师的 AI 副脑，推动风湿病诊疗的智慧化进程，提升诊疗效率并改善患者预后。'
          }
        },
        painPoints: {
          title: '痛点洞察与解方',
          sparcc: {
            title: 'SPARCC 评分系统',
            step1: '强直性脊柱炎',
            step2: '慢性风湿类疾病',
            step3: '引发炎症',
            assessment: '炎症评估',
            grading: '评估骶髂关节与脊柱的炎症程度',
            scoring: '在STIR-MRI上运用SPARCC评分系统',
            note: 'SPARCC 评分系统是最常用的'
          },
          traditional: {
            title: '传统 SPARCC 评分两大痛点',
            inefficiency: {
              title: '效率低落',
              description: '传统人工 SPARCC 评分平均耗时 8–12 分钟，难以满足快速诊断的需求，影响临床效率与患者体验。'
            },
            reliability: {
              title: '信度不足',
              description: '多中心研究显示，人工评分的组间一致性 κ 仅为 0.62，评分差异大，影响临床决策的准确性。'
            }
          },
          ai: {
            title: '核心功能特点',
            automation: {
              title: '自动化评分',
              description: 'AI 系统可一键上传 MRI-STIR 影像，自动分割脊柱节段与双侧骶髂关节，实现全程自动化评分。'
            },
            efficiency: {
              title: '高效评分',
              description: 'AI 系统在几秒内完成评分，并生成热图与结构化报告，大幅提升评分效率。'
            },
            precision: {
              title: '高精度',
              description: 'AI 评分结果与风湿科及影像科专家评分的 ICC 高达 0.80（脊柱）及 0.83（骶髂关节），确保评分的高精度与可靠性。'
            },
            objective: {
              title: '客观可重复',
              description: 'AI 系统提供即时、客观、可重复的评分结果，为临床决策提供可靠的数据支持。'
            }
          }
        },
        productShowcase: {
          title: '产品功能展示',
          subtitle: '体验SPARCC AI医学影像分析平台的强大功能',
          tabs: {
            spine: '脊柱分析',
            spineDesc: 'SPARCC脊柱炎症评估',
            si: 'SI关节分析',
            siDesc: '骶髂关节炎症评分',
            system: '系统界面',
            systemDesc: '平台操作界面展示'
          },
          images: {
            spine1: {
              alt: '脊柱分析结果1',
              title: '脊柱分割与评分',
              description: 'AI自动分割脊柱节段，提供精确的SPARCC评分'
            },
            spine2: {
              alt: '脊柱分析结果2',
              title: '脊柱炎症热图',
              description: '可视化炎症区域，辅助临床诊断决策'
            },
            si: {
              alt: 'SI关节分析结果',
              title: '骶髂关节评分',
              description: '四象限评分系统，精确评估骶髂关节炎症程度'
            },
            sys1: {
              alt: '系统界面1',
              title: '分析统计界面',
              description: '实时分析统计，支持多语言切换'
            },
            sys2: {
              alt: '系统界面2',
              title: '上传分析界面',
              description: '支持多种医学影像格式，一键上传分析'
            }
          },
          highlights: {
            title: '核心功能特点',
            speed: {
              title: '快速分析',
              description: '3秒内完成SPARCC评分，大幅提升诊断效率'
            },
            accuracy: {
              title: '高精度评分',
              description: 'ICC高达0.80-0.83，与专家评分高度一致'
            },
            automation: {
              title: '全自动化',
              description: '一键上传，自动分割，无需人工干预'
            },
            visualization: {
              title: '可视化报告',
              description: '热图显示炎症区域，结构化报告便于临床决策'
            }
          }
        },
        publication: {
          title: '学术发表',
          subtitle: '基于SPARCC评分系统的深度学习算法研究成果',
          authors: '作者',
          published: '已发表',
          pub1: {
            title: '基于深度学习的骶髂关节MRI SPARCC评分系统算法研究',
            journal: 'Journal of Magnetic Resonance Imaging'
          },
          pub2: {
            title: '中轴型脊柱关节炎MRI脊柱炎症的深度神经网络研究',
            journal: 'European spine journal'
          },
          pub3: {
            title: '中轴型脊柱关节炎炎症性骶髂关节炎磁共振成像的深度学习算法',
            journal: 'Rheumatology'
          },
          pub4: {
            title: '基于深度学习的中轴型脊柱关节炎脊柱炎症半定量评估算法',
            journal: 'RMD Open'
          },
          highlight: {
            title: '研究亮点',
            papers: '篇论文',
            period: '发表时间'
          }
        },
        productInfo: {
          title: '产品信息',
          subtitle: '我们 AI 医学影像平台的监管披露信息',
          relatedNotes: {
            title: '相关说明',
            intro: '本平台面向影像科（放射科）与风湿免疫科医生。AI 用于辅助评估脊柱和骶髂关节的炎症情况，并自动计算 SPARCC 炎症评分。',
            warning: 'AI 分析结果仅供医疗专业人员参考，不能替代医师的正式临床诊断。',
            validation: '算法有效性已通过同行评审临床研究验证，研究列表见下方。',
          },
          algorithmTraining: {
            label: '算法训练摘要',
            brief: '基于 U-Net 神经网络训练病灶分割模型，训练方法详见右侧摘要。',
            body: 'AI 模型基于 U-Net 卷积神经网络，用于分割脊柱 MRI 影像中的病灶。训练时，连续三张切片合并为一幅三通道图像，统一缩放至 128 × 128 像素；损失函数由 Dice 系数和加权二元交叉熵组成，并加大病灶像素的权重，避免因病灶区域太小而漏检；预测结果再经 0.5 阈值过滤，去掉置信度较低的部分。',
            fullSummaryLabel: '查看完整摘要',
            fullText: '本研究建立了一套结合深度学习影像分割及规则式影像分析的自动化算法，用于辨识脊柱磁共振影像中的病灶，并进一步计算与 SPARCC 评分相关的影像特征。'
              + '\n\n首先，病灶分割部分采用已训练的 U-Net 深度学习模型。输入影像会调整至 128 × 128 像素，并把相连三张影像分别放入不同的彩色三通道内，从而组成三通道影像，并配合相应的病灶标注掩膜进行模型推理及效能评估。模型相关设定包括 Dice 相似系数（Dice coefficient）、加权二元交叉熵（weighted binary cross-entropy），以及由两者组成的自定义损失函数。当中病灶像素会被赋予较高权重，以减低背景与病灶像素数量不平衡所造成的影响。模型输出其后会以 0.5 作为阈值进行后处理，以移除置信度较低的预测像素。'
              + '\n\n模型表现主要通过 Dice 相似系数评估病灶分割结果，并进一步利用混淆矩阵（confusion matrix）计算真正例、真反例、假正例及假反例。系统亦会建立受试者工作特征曲线（receiver operating characteristic curve，ROC curve），并计算曲线下面积（area under the curve，AUC），用以评估算法区分有病灶及无病灶影像的能力。此外，程序亦会比较真实标注掩膜与预测掩膜内各个独立病灶轮廓的重叠程度，以分析病灶层级的检测表现。'
              + '\n\n完成病灶分割后，系统会再通过影像处理算法进行 SPARCC 相关评分。第一阶段利用椎间盘掩膜及骨骼掩膜找出不同脊柱解剖区域，并通过轮廓、外接矩形、中心点及连通组件分析，将影像划分成不同区域。其后，每个解剖区域会与病灶掩膜进行重叠分析：若区域内没有病灶重叠，记为 0 分；若存在病灶重叠，记为 1 分；若同一区域内出现多个独立病灶连通组件，则记为 2 分。各区域分数最后会按影像名称合并，以形成每张影像的第一阶段 SPARCC 相关分数。'
              + '\n\n第二阶段主要分析病灶深度。算法会根据椎间盘轮廓计算相邻椎体之间的中心位置，并建立与脊柱方向垂直的参考线。同时，系统亦会计算相邻解剖参考点之间的平均距离。其后，再根据病灶连通组件的质心位置，以及病灶与参考线之间的空间关系，判断病灶是否符合深部病灶的条件，并以 0 或 1 的二元方式记录深度特征。'
              + '\n\n最后，算法亦加入病灶强度比较。系统会先根据影像名称整合病灶区域的强度数据，并取得每张影像中病灶的最大强度值，再与相对应的参考强度值进行比较。根据两者的相对大小，系统会将强度条件转换为 0 或 1 的二元结果，作为后续 SPARCC 相关评分的一部分。'
              + '\n\n整体而言，此方法形成了一个由“深度学习病灶分割、分割效能评估、解剖区域划分、病灶位置及数量分析、病灶深度分析，以及病灶强度分析”所组成的自动化流程，目标是将磁共振影像中的病灶分割结果，进一步转化为具临床意义的 SPARCC 相关量化指标。',
          },
        },
        contact: {
          title: '联系我们',
          getInTouch: '与我们联系',
          description: '对我们的平台有疑问或需要支持？我们在此协助医疗专业人员充分利用我们的AI分析工具。',
          email: {
            title: '电子邮件支持',
            description: '技术支持和一般查询',
            ariaLabel: '发送电子邮件给支持团队'
          },
          business: {
            title: '商业查询',
            description: '合作伙伴关系和企业解决方案',
            ariaLabel: '发送商业查询电子邮件'
          },
          phone: {
            title: '联系电话',
            description: '学术合作伙伴关系和研究项目',
            ariaLabel: '致电咨询'
          },
          company: {
            title: 'SPARCC 平台',
            type: '医学AI技术公司',
            location: '为全球医疗专业人员提供服务',
            availability: '24/7 平台可用性'
          }
        }
      },
      navigation: {
        backToHomepage: '返回首页',
        homepage: '首页',
        analysisApp: '分析平台',
        upload: '上传',
        query: '查询'
      },
      home: {
        loadingText: '系统启动中...',
        totalAnalyses: '总分析次数',
        todayAnalyses: '今日分析',
        avgTime: '平均处理时间',
        fullScan: '完整扫描',
        fullScanDesc: '上传单个医学影像文件(NII/NII.GZ/DCM/DICOM)或同一患者的连续扫描图片',
        partialScan: '非完整扫描',
        partialScanDesc: '非连续性扫描图片分析，可以是单张或多人多张',
        queryRecords: '查询分析记录',
        queryRecordsDesc: '输入身份证号查询历史记录',
        totalAnalysesLabel: '总分析次数',
        todayAnalysesLabel: '今日分析',
        monthAnalysesLabel: '本月分析',
        systemStatistics: '系统统计',
        uploadMedicalImage: '上传医学影像进行AI分析',
        batchUploadMedicalImage: '批量上传医学影像进行AI分析',
        queryHistoryRecords: '输入身份证号查询历史分析记录'
      },
      upload: {
        titleFull: '完整扫描分析',
        titlePartial: '非完整扫描分析',
        subtitleFull: '上传单个医学影像文件(NII/NII.GZ/DCM/DICOM)或同一患者的多张JPG扫描图片',
        subtitlePartial: '上传JPG或DCM/DICOM文件进行非连续性扫描分析',
        tip: '注意：上传的图片有可能会被作用于ai的进一步训练',
        dataConfidentiality: '您上传的所有数据都将被严格保密，仅用于指定的处理目的。',
        chooseFiles: '选择文件',
        selectedCount: '已选择 {{count}} 个文件',
        addMore: '添加更多',
        start: '开始分析',
        uploading: '上传中...',
        placeholderTitle: '拖拽文件到此处或点击选择',
        placeholderTipFull: '支持单个 NII/NII.GZ(最大150MB)、DCM/DICOM(最大50MB)，或多个 JPG 图片(最大10MB)',
        completeToast: '批量分析完成！成功: {{success}}, 失败: {{failed}}',
        uploadingBusy: '上传正在进行中，请稍后...',
        selectFormats: '请选择支持的文件格式：JPG、NII/NII.GZ、DCM、DICOM',
        selectRule: '请选择一个NII/NII.GZ文件、多个DCM/DICOM文件或多个JPG图片，不能混合选择',
        onlyOneMedical: '医学影像文件(NII/NII.GZ/DCM/DICOM)只能选择一个',
        onlyOneNII: 'NII/NII.GZ文件只能选择一个',
        tooManyMedicalFiles: '最多只能选择 {{max}} 个医学影像文件',
        fileTooLarge: '文件 {{file}} 大小超出限制，最大允许 {{size}}MB',
        tooManyImages: '非完整扫描模式下，单次上传的图片数量不能超过 {{max}} 张，当前选择了 {{count}} 张',
        heicNotSupported: '当前暂不支持 HEIC 图片，请在相册中导出为 JPG 后再上传',
        pleaseSelect: '请先选择文件',
        failed: '批量上传失败，请重试',
        analysisMissing: '分析结果未找到，无法保存患者信息',
        patientRequired: '患者姓名和身份证号是必填项',
        patientUpdateSuccess: '患者信息更新成功',
        patientUpdateFail: '保存患者信息失败，请重试',
        partialUploadText: '上传JPG图片',
        fullUploadText: '上传医学影像或图片',
        partialFileTypes: '支持JPG（单个最大10MB）、DCM/DICOM（单个最大50MB）',
        fullFileTypes: '支持NII/NII.GZ/DCM/DICOM或JPG，详见大小限制',
        startAnalyze: '开始分析',
        reset: '重置',
        saveInfo: '保存信息',
        backUpload: '返回上传',
        analysisResultNotFound: '分析结果不存在，无法保存患者信息',
        patientNameIdRequired: '患者姓名和身份证号不能为空',
        patientInfoUpdateSuccess: '患者信息更新成功',
        patientInfoUpdateFail: '保存患者信息失败',
        analysisFailed: '分析失败: {{error}}',
        analysisException: '分析返回异常',
        networkRecovered: '网络已恢复，可以重试保存',
        retryWithCount: '，点击重试按钮重新尝试'
      },
      query: {
        title: '医学影像分析查询',
        placeholder: '搜索患者姓名、身份证号、医生工号或医生姓名...',
        searching: '正在查询数据库...',
        btnSearch: '搜索',
        noResults: '未找到相关记录',
        trySearching: '尝试搜索患者姓名或医生姓名',
        retrySearch: '重新搜索',
        foundCount: '找到 {{count}} 个分析会话',
        totalCount: '共找到 {{count}} 条记录',
        prev: '上一页',
        next: '下一页',
        pageInfo: '{{current}}/{{total}}',
        doctor: '医生',
        images: '{{count}}张',
        completed: '已完成',
        processing: '处理中',
        failed: '失败',
        high: '高风险',
        normal: '中风险',
        low: '低风险',
        searchFail: '查询失败，请稍后重试',
        loadMoreFail: '加载更多结果失败',
        yesterday: '昨天',
        daysAgo: ' 天前',
        weeksAgo: ' 周前',
        unknownTime: '未知时间',
        summary: '概要',
        detail: '详情',
        unknownPatient: '未知患者',
        systemDoctor: '系统医生',
        time: '时间',
        sparccScore: 'SPARCC 评分:',
        tabAll: '全部',
        tabToday: '今日',
        tabWeek: '本周',
        tabMonth: '本月',
        today: '今天',
        loadingSearch: '搜索中...',
        noSearchResults: '暂无搜索结果',
        trySearchTip: '尝试搜索患者姓名或医生姓名',
        loadMore: '加载更多',
        loadingMore: '加载中...',
        search: '搜索'
      },
      feedback: {
        title: '用户反馈',
        sectionTitle: '用户建议',
        description: '请填写您对分析结果的反馈，有助于我们改进系统',
        userSuggestion: '用户建议',
        noduleDetectionRate: '结节探查率',
        scoringAccuracy: '评分准确率',
        comments: '意见',
        commentsPlaceholder: '请提供您的建议和反馈...',
        satisfied: '满意',
        average: '一般',
        unsatisfied: '不满意',
        submit: '提交反馈',
        submitting: '提交中...',
        submitSuccess: '反馈提交成功！',
        submitError: '反馈提交失败，请重试',
        required: '此项为必填项',
        commentsMaxLength: '意见不能超过300个字符',
        networkError: '网络错误，请检查网络连接',
        validationError: '请填写所有必填项',
        thankYou: '感谢您的反馈！'
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // 防止 zh-CN 回退到 zh（繁体），统一只回退到英文
    fallbackLng: {
      default: ['en'],
      zh: ['en'],
      'zh-CN': ['en'],
    },
    // 仅加载当前精确语言（如 zh-CN 不再向上查找 zh）
    load: 'currentOnly',
    // 明确声明支持的语言，避免其它地区代码干扰
    supportedLngs: ['en', 'zh', 'zh-CN'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      caches: ['localStorage']
    },
  });

export default i18n;

